import type { AddressDict } from "../types/actions";

const KEY = (cartToken: string) => `miraq_address_draft:${cartToken}`;

/**
 * Copy of an address without the contact fields (email, phone). Browser
 * storage keeps the name/address so a reload doesn't wipe the form, but never
 * the shopper's email or phone number (see the Browser Storage section of the
 * privacy policy).
 */
export function withoutContact(draft: AddressDict): AddressDict {
  const { email: _email, phone: _phone, ...rest } = draft;
  return rest;
}

/**
 * Load a previously saved address draft from sessionStorage.
 * Returns null if not found, storage is unavailable, or the token is null.
 */
export function loadAddressDraft(cartToken: string | null): AddressDict | null {
  if (!cartToken) return null;
  try {
    const raw = sessionStorage.getItem(KEY(cartToken));
    if (!raw) return null;
    return JSON.parse(raw) as AddressDict;
  } catch {
    return null;
  }
}

/**
 * Persist an address draft to sessionStorage keyed by cart token.
 * Silently ignores errors (storage full, private browsing restrictions, etc.).
 */
export function saveAddressDraft(
  cartToken: string | null,
  draft: AddressDict,
): void {
  if (!cartToken) return;
  try {
    sessionStorage.setItem(
      KEY(cartToken),
      JSON.stringify(withoutContact(draft)),
    );
  } catch {
    // sessionStorage can throw in some embedded/private contexts — ignore
  }
}

/**
 * Remove the address draft for the given cart token.
 */
export function clearAddressDraft(cartToken: string | null): void {
  if (!cartToken) return;
  try {
    sessionStorage.removeItem(KEY(cartToken));
  } catch {
    // ignore
  }
}
